'use client';
import React, { useState } from 'react';
import { Send, Phone } from 'lucide-react';

const conversations = [
  {
    id: 1,
    priest: 'Pandit Ramesh Kumar',
    ritual: 'Ganesh Puja',
    lastMessage: 'Please keep the puja items ready by 8:30 AM.',
    time: '10 min ago',
    unread: 1,
    emoji: '🙏',
    messages: [
      { from: 'priest', text: 'Namaste! I have confirmed your Ganesh Puja booking for Aug 7.', time: '9:00 AM' },
      { from: 'me', text: 'Thank you Panditji. What items should I prepare?', time: '9:05 AM' },
      { from: 'priest', text: 'Please arrange: flowers, coconut, incense sticks, camphor, and fruits. I will bring the rest.', time: '9:10 AM' },
      { from: 'me', text: 'Understood. Will everything be ready.', time: '9:12 AM' },
      { from: 'priest', text: 'Please keep the puja items ready by 8:30 AM.', time: '10:00 AM' },
    ],
  },
  {
    id: 2,
    priest: 'Pandit Arun Joshi',
    ritual: 'Navgraha Shanti',
    lastMessage: 'The puja was completed successfully. 🙏',
    time: '2 days ago',
    unread: 0,
    emoji: '🌟',
    messages: [
      { from: 'priest', text: 'Namaste! I am on my way for the Navgraha Shanti Puja.', time: '7:30 AM' },
      { from: 'me', text: 'Thank you Panditji. We are ready.', time: '7:35 AM' },
      { from: 'priest', text: 'The puja was completed successfully. 🙏', time: '12:00 PM' },
    ],
  },
];

export default function Messages() {
  const [activeConv, setActiveConv] = useState(conversations[0]);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState(activeConv.messages);

  const handleSelectConv = (conv: typeof conversations[0]) => {
    setActiveConv(conv);
    setMessages(conv.messages);
    setNewMessage('');
  };

  const handleSend = () => {
    if (!newMessage.trim()) return;
    setMessages(prev => [...prev, { from: 'me', text: newMessage.trim(), time: 'Just now' }]);
    setNewMessage('');
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-5">
        <h2 className="font-display text-xl font-bold text-maroon">Messages</h2>
        <p className="text-sm text-maroon/50 mt-0.5">Communicate with your priests</p>
      </div>

      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden flex" style={{ height: '520px' }}>
        {/* Conversation List */}
        <div className="w-64 border-r border-gold/10 flex flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gold/10">
            <p className="text-xs font-semibold text-maroon/50 uppercase tracking-wide">Conversations</p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gold/10">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => handleSelectConv(conv)}
                className={`w-full px-4 py-3.5 text-left hover:bg-cream/40 transition-colors ${activeConv.id === conv.id ? 'bg-saffron/5 border-r-2 border-saffron' : ''}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-9 h-9 rounded-full saffron-gradient flex items-center justify-center text-base flex-shrink-0">
                    {conv.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-xs font-semibold text-maroon truncate">{conv.priest}</p>
                      {conv.unread > 0 && (
                        <span className="w-4 h-4 rounded-full bg-saffron text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                          {conv.unread}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-maroon/40 mt-0.5">{conv.ritual}</p>
                    <p className="text-[10px] text-maroon/50 mt-0.5 truncate">{conv.lastMessage}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat Header */}
          <div className="px-4 py-3 border-b border-gold/10 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full saffron-gradient flex items-center justify-center text-sm">
                {activeConv.emoji}
              </div>
              <div>
                <p className="text-sm font-semibold text-maroon">{activeConv.priest}</p>
                <p className="text-[10px] text-maroon/40">{activeConv.ritual}</p>
              </div>
            </div>
            <a href="tel:+919876543210" className="flex items-center gap-1.5 text-xs font-medium text-maroon/50 hover:text-saffron transition-colors">
              <Phone size={14} />
            </a>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.from === 'me' ?'bg-saffron text-white rounded-br-sm' :'bg-cream text-maroon rounded-bl-sm'
                }`}>
                  <p>{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${msg.from === 'me' ? 'text-white/60' : 'text-maroon/40'}`}>{msg.time}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-gold/10 flex items-center gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Type a message…"
              className="flex-1 text-sm text-maroon placeholder-maroon/30 bg-cream/50 border border-gold/20 rounded-full px-4 py-2 outline-none focus:border-saffron/40"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim()}
              className="w-9 h-9 rounded-full bg-saffron text-white flex items-center justify-center hover:bg-saffron/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
